import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';
import { LayoutDashboard, Users, Download, UserCog, Settings, LogOut, Building2 } from 'lucide-react';

export default function Layout() {
  const { t, i18n } = useTranslation();
  const { user, logout, activeOffice, setActiveOffice, switchLanguage } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/login'); };

  const navItems = [
    { to: '/', icon: <LayoutDashboard size={18} />, label: t('dashboard'), exact: true },
    { to: '/clients', icon: <Users size={18} />, label: t('clientDatabase') },
    { to: '/downloads', icon: <Download size={18} />, label: t('downloads') },
    ...(user?.role === 'admin' ? [{ to: '/users', icon: <UserCog size={18} />, label: t('userManagement') }] : []),
    { to: '/settings', icon: <Settings size={18} />, label: t('settings') },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <div style={{
        width: 240, background: 'var(--primary)', display: 'flex',
        flexDirection: 'column', padding: '0', position: 'fixed',
        top: 0, left: 0, bottom: 0, zIndex: 100
      }}>
        {/* Logo */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ color: 'white', fontWeight: 800, fontSize: 18 }}>FINsathi</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginTop: 2 }}>IT Portal</div>
        </div>

        {/* Office Toggle */}
        {user?.office_access === 'both' && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              {t('office')}
            </div>
            <div style={{ display: 'flex', background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: 3 }}>
              {['ahmedabad', 'palanpur'].map(o => (
                <button key={o} onClick={() => setActiveOffice(o)} style={{
                  flex: 1, padding: '5px 8px', border: 'none', borderRadius: 6, fontSize: 12,
                  fontWeight: activeOffice === o ? 700 : 400, cursor: 'pointer', transition: 'all 0.15s',
                  background: activeOffice === o ? 'white' : 'none',
                  color: activeOffice === o ? 'var(--primary)' : 'rgba(255,255,255,0.7)',
                }}>
                  {o === 'ahmedabad' ? t('ahmedabad') : t('palanpur')}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Nav Links */}
        <nav style={{ flex: 1, padding: '12px 12px' }}>
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.exact}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 8, marginBottom: 2,
                color: isActive ? 'white' : 'rgba(255,255,255,0.65)',
                background: isActive ? 'rgba(255,255,255,0.15)' : 'none',
                textDecoration: 'none', fontSize: 14, fontWeight: isActive ? 600 : 400,
                transition: 'all 0.15s',
              })}>
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          {/* Language Toggle */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {['en', 'gu'].map(lang => (
              <button key={lang} onClick={() => switchLanguage(lang)} style={{
                flex: 1, padding: '5px', border: 'none', borderRadius: 6, fontSize: 11,
                cursor: 'pointer', background: i18n.language === lang ? 'rgba(255,255,255,0.2)' : 'none',
                color: i18n.language === lang ? 'white' : 'rgba(255,255,255,0.5)',
                fontWeight: i18n.language === lang ? 600 : 400,
              }}>
                {lang === 'en' ? 'EN' : 'ગુ'}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ color: 'white', fontSize: 13, fontWeight: 600 }}>{user?.username}</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, textTransform: 'capitalize' }}>{user?.role}</div>
            </div>
            <button onClick={handleLogout} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)',
              cursor: 'pointer', padding: 6, borderRadius: 6,
            }} title={t('logout')}>
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ marginLeft: 240, flex: 1, padding: '28px 32px', minHeight: '100vh' }}>
        <Outlet />
      </div>
    </div>
  );
}
